import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { Order, ItemState } from '@pegasus/types/order';
import { OrderReturnResponse } from '@pegasus/types/session';
import {
    State,
    fetchOrderAndSession,
    updateSession,
    toggleItem,
    selectReason,
    markItemsInvalid,
    markItemsValid,
} from './actions';
import { ReturnReasonList } from '../types';

const initialState: State = {
    list: {},
    isLoading: false,
    isUpdateLoading: false,
};

const reduceState = (order: Order, session: OrderReturnResponse) => {
    const list = order.items.reduce<ReturnReasonList>((returnReasonList, item) => {
        const itemInSession = session.items.find(sessionItem => item.id === sessionItem.id);

        returnReasonList[item.id] = {
            isSelected: !!itemInSession,
            isReturnable:
                Boolean(order.is_order_returnable) && item.item_state === ItemState.SHIPPED,
            isError: false,
            reason: itemInSession ? itemInSession.return_reason : undefined,
            itemId: item.id,
        };

        return returnReasonList;
    }, {});

    return {
        list,
        order,
        session,
    };
};

export const reducer = reducerWithInitialState<State>(initialState)
    .case(fetchOrderAndSession.async.started, state => ({
        ...state,
        isLoading: true,
    }))
    .case(fetchOrderAndSession.async.failed, state => ({
        ...state,
        isLoading: false,
    }))
    .case(fetchOrderAndSession.async.done, (state, { result: { order, session } }) => ({
        ...state,
        isLoading: false,
        ...reduceState(order.results, session),
    }))
    .case(updateSession.async.started, state => ({
        ...state,
        isUpdateLoading: true,
    }))
    .case(updateSession.async.failed, state => ({
        ...state,
        isUpdateLoading: false,
    }))
    .case(updateSession.async.done, (state, { result: { session } }) => ({
        ...state,
        isUpdateLoading: false,
        ...reduceState(state.order as Order, session),
    }))
    .case(toggleItem, (state, { itemId }) => ({
        ...state,
        list: {
            ...state.list,
            [itemId]: {
                ...state.list[itemId],
                isSelected: !state.list[itemId].isSelected,
            },
        },
    }))
    .case(selectReason, (state, { itemId, reason }) => ({
        ...state,
        list: {
            ...state.list,
            [itemId]: {
                ...state.list[itemId],
                reason,
                isSelected: true,
            },
        },
    }))
    .case(markItemsInvalid, state => ({
        ...state,
        list: Object.values(state.list).reduce<ReturnReasonList>((returnReasonList, item) => {
            returnReasonList[item.itemId] = {
                ...item,
                isError: item.isSelected && !item.reason,
            };

            return returnReasonList;
        }, {}),
    }))
    .case(markItemsValid, state => ({
        ...state,
        list: Object.values(state.list).reduce<ReturnReasonList>((returnReasonList, item) => {
            returnReasonList[item.itemId] = {
                ...item,
                isError: false,
            };
            return returnReasonList;
        }, {}),
    }));
